import { Z_SIGNAL_QUALITY } from '../config/zIndexLayers';

/**
 * The host's reading of the PLAYER-VISIBLE LATENCY (calibrated glass-to-glass), mapped onto
 * the bar tiers on the host: `ideal` ≤ 30 ms, `degraded` ≤ 100 ms, `worst` above that.
 * `none` means no reading exists (not measured yet, or the transport is gone).
 */
export type SignalQualityLevel = 'ideal' | 'degraded' | 'worst' | 'none';

/**
 * Coarse HUD state. Component-local only — deliberately NOT exported
 * cross-module so this phase introduces no new shared contract.
 *
 * `degraded` (mild) and `worst` (lowest quality) are DISTINCT tiers so the
 * HUD can show a clearer quality scale instead of collapsing both into one.
 */
type CoarseSignalState = 'good' | 'degraded' | 'worst' | 'reconnecting';

export type SignalQualityIndicatorProps = {
  /** The host-published latency level from the player store. */
  level: SignalQualityLevel | null | undefined;
  /** When true (ICE down / reconnecting), force the reconnecting state. */
  connectionDown?: boolean;
  /**
   * User override from the mobile settings menu. `false` hides the bars entirely;
   * `true`/undefined shows them whenever a reading exists (the default — the HUD is a
   * production surface, not a debug one).
   */
  enabled?: boolean;
};

/**
 * Derive the coarse HUD state purely from existing store state:
 *   - reconnecting: connection is down / reconnecting
 *   - worst:        latency over 100 ms
 *   - degraded:     latency over 30 ms, up to 100 ms
 *   - good:         latency at or under 30 ms
 *   - null:         no reading exists yet — draw nothing rather than guess
 */
function deriveCoarseState(
  level: SignalQualityLevel | null | undefined,
  connectionDown: boolean,
): CoarseSignalState | null {
  if (connectionDown) return 'reconnecting';
  if (level === 'worst') return 'worst';
  if (level === 'degraded') return 'degraded';
  if (level === 'ideal') return 'good';
  return null;
}

/** Bar color per coarse tier (reconnecting reuses the alert red for its icon). */
const STATE_COLOR: Record<CoarseSignalState, string> = {
  good: '#22c55e', // green-500
  degraded: '#eab308', // yellow-500
  worst: '#ef4444', // red-500
  reconnecting: '#ef4444', // red-500
};

/** Lit bar count per coarse tier (reconnecting renders the no-signal icon). */
const STATE_BARS: Record<CoarseSignalState, number> = {
  good: 3,
  degraded: 2,
  worst: 1,
  reconnecting: 0,
};

/**
 * Coarse latency HUD, pinned upper-left.
 * good = green 3 bars; degraded = yellow 2 bars (pulsing);
 * worst = red 1 bar (pulsing); reconnecting = no-signal icon (pulsing).
 * Renders nothing until the host has published a latency reading — a link nobody has
 * measured yet must not show three green bars. Once a reading exists it never auto-hides
 * and never fades on a healthy stream, so `good` stays visible indefinitely.
 *
 * The `enabled` prop is the mobile-settings toggle: ON by default, OFF hides the HUD
 * entirely (the host keeps publishing; only the drawing stops).
 *
 * Positioned `fixed` at Z_SIGNAL_QUALITY so it needs no positioned ancestor
 * and stays visible above the opaque reconnect cover (Z_RECONNECT), which
 * would otherwise hide the HUD during the exact window it reports on.
 */
export function SignalQualityIndicator({
  level,
  connectionDown = false,
  enabled = true,
}: SignalQualityIndicatorProps) {
  if (enabled === false) return null;
  const state = deriveCoarseState(level, connectionDown);
  if (state === null) return null;

  const isAlert = state !== 'good';
  const color = STATE_COLOR[state];
  const bars = STATE_BARS[state];

  return (
    <div
      className={`pointer-events-none fixed left-11 top-3 ${isAlert ? 'signal-quality-pulse' : ''}`}
      data-testid='signal-quality-indicator'
      data-state={state}
      data-bars={bars}
      aria-label={`Signal quality: ${state}`}
      style={{ color, zIndex: Z_SIGNAL_QUALITY }}
    >
      <svg width='28' height='20' viewBox='0 0 28 20' fill='currentColor' aria-hidden='true'>
        {state === 'reconnecting' ? (
          <>
            <rect x='2' y='14' width='4' height='4' rx='0.5' opacity='0.35' />
            <rect x='8' y='10' width='4' height='8' rx='0.5' opacity='0.35' />
            <rect x='14' y='6' width='4' height='12' rx='0.5' opacity='0.35' />
            <line x1='2' y1='2' x2='26' y2='18' stroke='currentColor' strokeWidth='2.5' />
          </>
        ) : (
          <>
            <rect x='2' y='14' width='4' height='4' rx='0.5' opacity={bars >= 1 ? 1 : 0.25} />
            <rect x='8' y='10' width='4' height='8' rx='0.5' opacity={bars >= 2 ? 1 : 0.25} />
            <rect x='14' y='6' width='4' height='12' rx='0.5' opacity={bars >= 3 ? 1 : 0.25} />
          </>
        )}
      </svg>
      <style>{`
        @keyframes signal-quality-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        .signal-quality-pulse {
          animation: signal-quality-pulse 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
